import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientLineDescription } from "../../../../../client/components/ClientLineDescription";
import { ClientFormType } from "../../../../../client/enums";
import { InvestmentContract } from "../../../../types";
import { ContractAgentsData } from "../../../ContractAgentsData";
import { ContractClientsData } from "../../../ContractClientsData";
import { createCancellationDateDescriptionItem } from "../../../description-items/cancellationDateDescriptionItem";
import { createEffectiveEndDateDescriptionItem } from "../../../description-items/effectiveEndDateDescriptionItem";
import { createNoteDescriptionItem } from "../../../description-items/noteDescriptionItem";

interface Props {
  contract: InvestmentContract;
}

export const InvestmentContractDataSection = ({ contract }: Props) => {
  const { t } = useTranslation();

  const client = contract.clients[contract.policyHolderIndex];

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: 180, fontWeight: "bold" } }}
      items={[
        {
          label: t("contract.sections.clients"),
          children: client && (
            <ContractClientsData
              items={[
                {
                  label: t(`client.enums.formType.${ClientFormType.POLICY_HOLDER}`),
                  children: <ClientLineDescription client={client} />,
                },
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.contractData"),
          children: (
            <Descriptions
              bordered
              size="small"
              column={3}
              styles={{ content: { whiteSpace: "nowrap" } }}
              items={[
                {
                  label: t("contract.attrs.mediationReportSignDate"),
                  children: contract.mediationReportSignDate
                    ? formatLocaleDate(contract.mediationReportSignDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.signDate"),
                  children: formatLocaleDate(contract.signDate),
                },
                createEffectiveEndDateDescriptionItem(contract),
                {
                  ...createCancellationDateDescriptionItem(contract),
                  span: 3,
                },
                {
                  label: t("contract.attrs.monthlyPayment"),
                  children: formatLocaleCurrency(contract.monthlyPayment),
                },
                {
                  label: t("contract.attrs.onetimePayment"),
                  children: formatLocaleCurrency(contract.onetimePayment),
                },
                {
                  label: t("contract.attrs.investmentDuration"),
                  children: contract.investmentDuration,
                },
                {
                  label: t("contract.enums.investmentType._label"),
                  children: t("contract.enums.investmentType." + contract.investmentType),
                },
                {
                  label: t("contract.enums.investmentRiskLevel._label"),
                  children: contract.riskLevel
                    ? t("contract.enums.investmentRiskLevel." + contract.riskLevel)
                    : undefined,
                },
                {
                  label: t("contract.enums.investmentComputedRiskLevel._label"),
                  children: contract.computedRiskLevel
                    ? t("contract.enums.investmentComputedRiskLevel." + contract.computedRiskLevel)
                    : undefined,
                },
                {
                  label: t("contract.attrs.prepaidTargetAmount"),
                  children: isDefined(contract.prepaidTargetAmount)
                    ? formatLocaleCurrency(contract.prepaidTargetAmount)
                    : undefined,
                },
                {
                  label: t("contract.attrs.feePaymentsCount"),
                  children: contract.feePaymentsCount,
                },
                {
                  label: t("contract.attrs.feeTotalAmount"),
                  children: formatLocaleCurrency(contract.feeTotalAmount),
                },
                {
                  label: t("contract.enums.investmentFeeType._label"),
                  children: t("contract.enums.investmentFeeType." + contract.feeType),
                },
                {
                  label: t("contract.attrs.managementFee"),
                  children: isDefined(contract.managementFee)
                    ? formatLocalePercentageNumber(contract.managementFee)
                    : undefined,
                },
                {
                  label: t("contract.attrs.pid"),
                  children: contract.pid,
                },
                {
                  label: t("contract.attrs.transferredFromOtherBroker"),
                  children: tBoolean(contract.transferredFromOtherBroker),
                },
                {
                  label: t("contract.attrs.transferredToOtherBroker"),
                  children: contract.transferredToOtherBrokerDate
                    ? formatLocaleDate(contract.transferredToOtherBrokerDate)
                    : undefined,
                  span: 2,
                },

                {
                  label: t("contract.attrs.offerLabel"),
                  children: contract.offerLabel,
                },
                {
                  label: t("contract.attrs.productInfoLink"),
                  children: <span style={{ whiteSpace: "normal" }}>{contract.productInfoLink}</span>,
                  span: 2,
                },
                createNoteDescriptionItem(contract),
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.agents"),
          children: <ContractAgentsData contract={contract} />,
        },
      ]}
    />
  );
};
